import { useLang } from "@/lib/lang";
import { LegalLayout, Section, BulletList } from "@/components/LegalLayout";

export default function Terms() {
  const { lang } = useLang();
  const T = lang === "fr" ? FR : EN;

  return (
    <LegalLayout title={T.title}>
      <h1 className="font-serif-mystery text-4xl md:text-5xl text-zinc-900 mb-2" data-testid="terms-title">{T.title}</h1>
      <p className="font-mono-data text-xs text-zinc-500 mb-8">{T.lastUpdate}</p>

      <p className="text-zinc-700 leading-relaxed mb-6">{T.intro}</p>

      <Section title={T.s1.title}>
        <p>{T.s1.body}</p>
        <BulletList items={T.s1.items} />
        <p>{T.s1.footer}</p>
      </Section>

      <Section title={T.s2.title}>
        <p>{T.s2.body}</p>
        <BulletList items={T.s2.items} />
      </Section>

      <Section title={T.s3.title}>
        {T.s3.body.map((p, i) => <p key={i}>{p}</p>)}
      </Section>

      <Section title={T.s4.title}>
        {T.s4.body.map((p, i) => <p key={i}>{p}</p>)}
      </Section>

      <Section title={T.s5.title}>
        <p>{T.s5.body}</p>
      </Section>

      <Section title={T.sSafety.title}>
        <p><strong>{T.sSafety.mission}</strong></p>
        <BulletList items={T.sSafety.items} />
        <p className="mt-3">{T.sSafety.footer}</p>
      </Section>

      <Section title={T.sAi.title}>
        <p>{T.sAi.body}</p>
      </Section>

      <Section title={T.contact.title}>
        <p className="font-mono-data text-sm"><span className="text-zinc-500">{T.contact.emailLabel} :</span> <a href="mailto:BLEUREEFOBERMAYR@GMAIL.COM" className="text-[#8b6f3d] hover:underline">BLEUREEFOBERMAYR@GMAIL.COM</a></p>
      </Section>
    </LegalLayout>
  );
}

const FR = {
  title: "Conditions d'utilisation",
  lastUpdate: "Dernière mise à jour : 2026",
  intro: "En utilisant The Only Map You Will Ever Need, vous acceptez les présentes conditions d'utilisation.",
  s1: { title: "Description du service",
        body: "Notre site propose une carte interactive regroupant des informations sur :",
        items: ["Des lieux historiques.", "Des civilisations anciennes.", "Des légendes.", "Des trésors.", "Des mystères.", "Des phénomènes inexpliqués.", "Des découvertes et théories exploratoires."],
        footer: "Certaines informations peuvent présenter des légendes, des croyances populaires ou des hypothèses qui ne sont pas confirmées scientifiquement. Le contenu est présenté dans un but informatif et de divertissement." },
  s2: { title: "Utilisation du site",
        body: "Les utilisateurs s'engagent à :",
        items: ["Utiliser le site de manière respectueuse.", "Ne pas tenter de nuire au fonctionnement du site.", "Ne pas publier de contenu illégal ou offensant dans les espaces de commentaires."] },
  s3: { title: "Exactitude des informations",
        body: ["Nous faisons des efforts pour fournir des informations intéressantes et pertinentes, mais nous ne garantissons pas que toutes les informations soient exactes, complètes ou toujours à jour.",
               "Les utilisateurs doivent effectuer leurs propres vérifications avant de prendre des décisions basées sur le contenu du site."] },
  s4: { title: "Propriété intellectuelle",
        body: ["Le contenu original créé pour ce site demeure la propriété de The Only Map You Will Ever Need.",
               "Toute reproduction importante du contenu sans autorisation est interdite."] },
  s5: { title: "Modification des conditions",
        body: "Nous pouvons modifier ces conditions d'utilisation à tout moment afin d'améliorer notre service." },
  sSafety: {
    title: "Responsabilité et sécurité des explorations",
    mission: "L'objectif de WorldMysteriesMap est d'encourager les gens à explorer le monde, découvrir l'histoire, le patrimoine, les légendes et les mystères de façon responsable.",
    items: [
      "Les utilisateurs sont entièrement responsables de leurs déplacements et de leurs décisions.",
      "WorldMysteriesMap, son propriétaire et ses développeurs ne peuvent être tenus responsables des blessures, accidents, pertes, dommages matériels, infractions, amendes ou tout autre incident pouvant survenir lors d'une visite.",
      "Les utilisateurs doivent toujours respecter les lois locales, les propriétés privées, les zones protégées et les consignes de sécurité.",
      "Les utilisateurs doivent faire preuve de jugement et de bon sens avant de visiter un lieu.",
      "Certains lieux peuvent être privés, dangereux ou interdits d'accès sans autorisation — nous ne recommandons jamais d'y pénétrer illégalement.",
    ],
    footer: "En utilisant ce site, vous reconnaissez avoir pris connaissance de ces règles et acceptez d'explorer à vos propres risques.",
  },
  sAi: {
    title: "Contenu généré par intelligence artificielle",
    body: "Certaines analyses, hypothèses et prédictions présentées sur ce site sont générées par une intelligence artificielle et sont clairement identifiées comme telles (badge « Hypothèse IA » ou « Prédictions IA »). Ces contenus ne constituent pas des faits historiques confirmés et ne doivent pas être considérés comme de la recherche scientifique validée. Ils servent uniquement à stimuler la curiosité et à ouvrir des pistes d'exploration.",
  },
  contact: { title: "Contact", emailLabel: "Courriel" },
};

const EN = {
  title: "Terms of Use",
  lastUpdate: "Last update: 2026",
  intro: "By using The Only Map You Will Ever Need, you accept these terms of use.",
  s1: { title: "Service description",
        body: "Our site offers an interactive map gathering information about:",
        items: ["Historical places.", "Ancient civilizations.", "Legends.", "Treasures.", "Mysteries.", "Unexplained phenomena.", "Discoveries and exploratory theories."],
        footer: "Some information may present legends, folk beliefs or hypotheses that are not scientifically confirmed. The content is provided for informational and entertainment purposes." },
  s2: { title: "Site usage",
        body: "Users agree to:",
        items: ["Use the site respectfully.", "Not attempt to harm the site's operation.", "Not post illegal or offensive content in comment areas."] },
  s3: { title: "Information accuracy",
        body: ["We strive to provide interesting and relevant information, but we do not guarantee that all information is accurate, complete or up to date.",
               "Users should perform their own verification before making decisions based on the site's content."] },
  s4: { title: "Intellectual property",
        body: ["Original content created for this site remains the property of The Only Map You Will Ever Need.",
               "Substantial reproduction of the content without authorization is prohibited."] },
  s5: { title: "Modification of terms",
        body: "We may modify these terms of use at any time to improve our service." },
  sSafety: {
    title: "Responsibility and exploration safety",
    mission: "The purpose of WorldMysteriesMap is to encourage people to explore the world, discover history, heritage, legends and mysteries responsibly.",
    items: [
      "Users are entirely responsible for their travel and their decisions.",
      "WorldMysteriesMap, its owner and developers cannot be held liable for injuries, accidents, losses, property damage, offences, fines or any other incident that may occur during a visit.",
      "Users must always respect local laws, private property, protected areas and safety guidelines.",
      "Users must apply judgment and common sense before visiting a location.",
      "Some places may be private, dangerous or restricted — we never encourage unlawful entry.",
    ],
    footer: "By using this site, you acknowledge that you have read these rules and agree to explore at your own risk.",
  },
  sAi: {
    title: "AI-generated content",
    body: "Some analyses, hypotheses and predictions displayed on this site are generated by artificial intelligence and are clearly labelled as such (\"AI hypothesis\" or \"AI prediction\" badge). These contents do not constitute confirmed historical facts and should not be treated as validated scientific research. They exist only to stimulate curiosity and open exploration paths.",
  },
  contact: { title: "Contact", emailLabel: "Email" },
};
